import { useEffect } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "sonner";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { updateSanction } from "../../../api/etudiants";
import { createFileAttachement } from "../../../api/fileAttachement";
import { ReceivedSanction, TypeOFSanction } from "./create_sanction_modal";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";

type Props = {
  show: boolean;
  sanction: any;
  metaData: any;
  onClose: () => void;
  onSaved?: () => void;
};

const EditSanctionModal = ({
  show,
  sanction,
  metaData,
  onClose,
  onSaved,
}: Props) => {
  const schema = z.object({
    studentId: z.string().nonempty("L'identifiant de l'étudiant est requis"),
    sanctionType: z.nativeEnum(TypeOFSanction),
    reason: z.string().nonempty("La raison de la sanction est requise"),
    sanctionReceived: z.nativeEnum(ReceivedSanction),
    duration: z.string().nonempty("La durée de la sanction est requise"),
    files: z.array(z.instanceof(File)).optional(),
    isCancel: z.boolean(),
  });

  type FormFields = z.infer<typeof schema>;

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(schema),
  });

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const formattedData = {
        type: data.sanctionType,
        reason_sanction: data.reason,
        sanction_received: data.sanctionReceived,
        duration: data.duration,
        id_studentProfil: data?.studentId || metaData?.Student?.id,
        cancels: data?.isCancel,
      };

      await updateSanction(sanction?.id, formattedData);

      if (files?.length > 0) {
        const formData = new FormData();
        formData.append("entity_type", "Sanction");
        formData.append("entity_id", sanction?.id);
        files?.forEach((file: any) => {
          formData.append("files", file);
        });
        await createFileAttachement(
          formData,
          buildPermissionContext(Resource.STUDENT_SANCTION, Action.UPDATE),
        );
      }

      reset();
      if (onSaved) onSaved();
      toast.success("Sanction modifiée !");
      onClose();
    } catch (error: any) {
      // console.log("Erreur lors de la modificaion de la sanction: ",error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setValue("files", watch("files")?.concat(Array.from(files)));
    }
  };

  const files = watch("files") || [];

  useEffect(() => {
    setValue("studentId", metaData?.Student?.id || "");
    setValue("sanctionType", sanction?.type || TypeOFSanction.Normal);
    setValue("reason", sanction?.reason_sanction || "");
    setValue(
      "sanctionReceived",
      sanction?.sanction_received || ReceivedSanction.Blame,
    );
    setValue("duration", sanction?.duration || "");
    setValue("files", []);
    setValue("isCancel", sanction?.cancels);
  }, [sanction, metaData]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Modifier une sanction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="sanction-edit-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Étudiant <span className="text-danger">*</span>:{" "}
                  </label>{" "}
                  {metaData?.Student ? (
                    <span>
                      {metaData.Student.firstname} {metaData.Student.lastname}
                    </span>
                  ) : null}
                  {errors.studentId && (
                    <div className="invalid-feedback">
                      {errors.studentId.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Type de sanction <span className="text-danger">*</span>
                  </label>
                  <select
                    {...register("sanctionType")}
                    aria-label="Default select example"
                    className={
                      errors.sanctionType
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                    defaultValue={sanction?.type || ""}
                  >
                    <option value={""}>Sélectionner</option>
                    {Object.values(TypeOFSanction).map((type) => (
                      <option key={type} value={type}>
                         {type === TypeOFSanction.Normal ? "Sanction Disciplinaire" : type}
                      </option>
                    ))}
                  </select>
                  {errors.sanctionType && (
                    <div className="invalid-feedback">
                      {errors.sanctionType.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Sanction reçu <span className="text-danger">*</span>
                  </label>
                  <select
                    {...register("sanctionReceived")}
                    aria-label="Default select example"
                    className={
                      errors.sanctionReceived
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                    defaultValue={sanction?.sanction_received || ""}
                  >
                    <option value={""}>Sélectionner</option>
                    {Object.values(ReceivedSanction).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.sanctionReceived && (
                    <div className="invalid-feedback">
                      {errors.sanctionReceived.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Raison de la sanction <span className="text-danger">*</span>
                    :{" "}
                  </label>
                  <br />
                  <textarea
                    {...register("reason")}
                    rows={3}
                    className={
                      errors.reason ? "form-control is-invalid" : "form-control"
                    }
                    defaultValue={sanction?.reason_sanction || ""}
                  />
                  {errors.reason && (
                    <div className="invalid-feedback">
                      {errors.reason.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Durée de la sanction <span className="text-danger">*</span>
                    :{" "}
                  </label>
                  <br />
                  <input
                    type="text"
                    {...register("duration")}
                    className={
                      errors.duration
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    defaultValue={sanction?.duration || ""}
                  />
                  {errors.duration && (
                    <div className="invalid-feedback">
                      {errors.duration.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">Annulée</label>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="flexSwitchCheckDefault"
                      {...register("isCancel")}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="flexSwitchCheckDefault"
                    >
                      {watch("isCancel") ? "Oui" : "Non"}
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Fichiers joints</label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="form-control"
                  />
                </div>
                {files?.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">
                      Nouveaux fichiers sélectionnés
                    </label>
                    <ul
                      className="list-group sc"
                      style={{ maxHeight: 200, overflowY: "auto" }}
                    >
                      {files.map((file: any, idx: number) => (
                        <li
                          key={idx}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>{file.name}</span>
                          <div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => {
                                const url = URL.createObjectURL(file);
                                window.open(url, "_blank");
                              }}
                            >
                              Voir
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                const newFiles = files.filter(
                                  (_, i) => i !== idx && !file.exist,
                                );
                                setValue("files", newFiles);
                              }}
                            >
                              Retirer
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-danger me-2"
                    onClick={() => {
                      reset();
                      onClose();
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    form="sanction-edit-form"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "En cours..." : "Enregistrer"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default withPermission(
  EditSanctionModal,
  [buildPermission(Resource.STUDENT_SANCTION, Action.UPDATE)],
  "masquer",
);
